import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const VEINS = 10;
const PETALS = 8;
const STAMENS = 9;
const RUNES = 6;
const RIPPLES = 3;
const BLOOM = 10;

function petalAt(g: Graphics, cx: number, cy: number, base: number, tip: number, a: number, spread: number, sx = 1, sy = 1): void {
  const mid = base + (tip - base) * 0.55;
  const ax = Math.cos(a);
  const ay = Math.sin(a);
  g.moveTo(cx + ax * base * sx, cy + ay * base * sy)
    .quadraticCurveTo(cx + Math.cos(a - spread) * mid * sx, cy + Math.sin(a - spread) * mid * sy, cx + ax * tip * sx, cy + ay * tip * sy)
    .quadraticCurveTo(cx + Math.cos(a + spread) * mid * sx, cy + Math.sin(a + spread) * mid * sy, cx + ax * base * sx, cy + ay * base * sy)
    .closePath();
}

function leaf(g: Graphics, x: number, y: number, s: number, rot: number): void {
  const ux = Math.cos(rot) * s;
  const uy = Math.sin(rot) * s;
  const vx = -uy * 0.42;
  const vy = ux * 0.42;
  g.moveTo(x - ux, y - uy)
    .quadraticCurveTo(x + vx, y + vy, x + ux, y + uy)
    .quadraticCurveTo(x - vx, y - vy, x - ux, y - uy)
    .closePath();
}

function ripple(g: Graphics, x: number, y: number, r: number, phase: number, amp: number): void {
  const steps = 48;
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * TAU;
    const rr = r + amp * Math.sin(5 * a + phase);
    const px = x + Math.cos(a) * rr;
    const py = y + Math.sin(a) * rr;
    if (i === 0) g.moveTo(px, py);
    else g.lineTo(px, py);
  }
}

function bloom(g: Graphics, body: Body, r: number, count: number, rot: number, base: number, spread: number): void {
  for (let i = 0; i < count; i++) {
    petalAt(g, body.x, body.y, base * r, r, rot + (i / count) * TAU, spread, body.scaleX, body.scaleY);
  }
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    for (let i = 0; i < VEINS; i++) {
      const a = (i / VEINS) * TAU + 0.31;
      g.moveTo(body.x + Math.cos(a) * r * 0.12 * sx, body.y + Math.sin(a) * r * 0.12 * sy).lineTo(
        body.x + Math.cos(a) * r * 0.78 * sx,
        body.y + Math.sin(a) * r * 0.78 * sy,
      );
    }
    g.stroke({ width: Math.max(1, r * 0.035), color: lerpColor(color, pal.ink, 0.45), alpha: 0.42 * alpha, cap: 'round' });
    g.ellipse(body.x, body.y, r * 0.8 * sx, r * 0.8 * sy).stroke({ width: Math.max(1, r * 0.05), color: lerpColor(color, pal.ink, 0.5), alpha: 0.45 * alpha });
    g.ellipse(body.x, body.y, r * 0.9 * sx, r * 0.9 * sy).stroke({ width: Math.max(1.2, r * 0.13), color: lerpColor(color, pal.rim, 0.5), alpha: 0.7 * alpha });
    g.circle(body.x, body.y, Math.max(1, r * 0.06)).fill({ color: lerpColor(color, pal.ink, 0.4), alpha: 0.5 * alpha });
    k.rimLight(body, 0.45 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    bloom(g, body, r * 0.93, PETALS, -Math.PI / 2, 0.08, 0.36);
    g.fill({ color: lerpColor(fill, pal.heldLight, 0.12), alpha: 0.9 * alpha });
    for (let i = 0; i < PETALS; i++) {
      const a = -Math.PI / 2 + ((i + 0.5) / PETALS) * TAU;
      g.moveTo(body.x + Math.cos(a) * r * 0.3 * body.scaleX, body.y + Math.sin(a) * r * 0.3 * body.scaleY).lineTo(
        body.x + Math.cos(a) * r * 0.86 * body.scaleX,
        body.y + Math.sin(a) * r * 0.86 * body.scaleY,
      );
    }
    g.stroke({ width: Math.max(1, r * 0.03), color: lerpColor(fill, pal.heldShade, 0.75), alpha: 0.55 * alpha, cap: 'round' });
    bloom(g, body, r * 0.64, PETALS, -Math.PI / 2 + Math.PI / PETALS, 0.1, 0.42);
    g.fill({ color: lerpColor(fill, pal.heldLight, 0.38), alpha: 0.92 * alpha });
    g.stroke({ width: Math.max(1, r * 0.022), color: lerpColor(fill, pal.heldShade, 0.6), alpha: 0.5 * alpha });
    g.ellipse(body.x, body.y, r * 0.2 * body.scaleX, r * 0.2 * body.scaleY).fill({ color: pal.spot, alpha });
    for (let i = 0; i < STAMENS; i++) {
      const a = (i / STAMENS) * TAU;
      g.circle(body.x + Math.cos(a) * r * 0.2 * body.scaleX, body.y + Math.sin(a) * r * 0.2 * body.scaleY, Math.max(0.8, r * 0.03));
    }
    g.fill({ color: lerpColor(pal.spot, pal.heldShade, 0.4), alpha: 0.85 * alpha });
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: pal.ink, alpha: 0.45 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    const gap = Math.max(3, k.unit * 0.02);
    for (let i = 0; i < RIPPLES; i++) {
      ripple(g, body.x, body.y, reach + i * gap, spin * 2 + i * 1.7, k.unit * 0.0035);
      g.stroke({ width: Math.max(1, 1.4 - i * 0.2), color: i === 0 ? pal.spot : pal.path, alpha: (0.55 - i * 0.14) * alpha });
    }
    const s = Math.max(3, k.unit * 0.02);
    k.ringDots(body, reach + gap, RUNES, spin, (x, y, _i, a) => {
      leaf(g, x, y, s, a + Math.PI / 2);
    });
    g.fill({ color: pal.spot, alpha: 0.8 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.incoming.circle;
    if (!on) {
      g.circle(x, y, radius * 0.75).stroke({ width: width * 0.8, color: pal.rim, alpha: 0.45 * alpha });
      return;
    }
    g.circle(x, y, radius * 2.1).fill({ color: tone, alpha: 0.14 * alpha });
    g.circle(x, y, radius * 1.4).fill({ color: tone, alpha: 0.22 * alpha });
    g.circle(x, y, radius * 0.85).fill({ color: lerpColor(tone, pal.rim, 0.3), alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(6, k.unit * 0.032);
    const amp = k.unit * 0.006;
    const flow = k.reduced ? 0 : k.time * 1.6;
    let along = 0;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      const n = Math.min(80, Math.floor(len / step));
      const nx = -dy / (len || 1);
      const ny = dx / (len || 1);
      for (let s = 1; s < n; s++) {
        const t = s / n;
        const w = Math.sin(((along + t * len) / step) * 1.1 - flow) * amp;
        g.circle(x0 + dx * t + nx * w, y0 + dy * t + ny * w, s % 3 === 0 ? 1.7 : 1.1);
      }
      along += len;
    }
    g.fill({ color: pal.path, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 2 === 0) petalAt(g, x, y, size * -0.9, size * 1.5, rot, 0.6);
    else g.circle(x, y, size * 0.7);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    g.circle(m.x, m.y, m.r).stroke({ width: m.width, color: pal.incoming.circle, alpha: m.fade * 0.36 });
    g.circle(m.x, m.y, m.r * 1.22).stroke({ width: m.width * 0.6, color: pal.spot, alpha: m.fade * 0.28 });
    const open = Math.min(1, m.grow / 0.2);
    const tip = m.r * (0.62 + 0.4 * open);
    for (let i = 0; i < BLOOM; i++) petalAt(g, m.x, m.y, m.r * 0.6, tip, (i / BLOOM) * TAU, 0.3);
    g.fill({ color: pal.held, alpha: m.fade * 0.24 * open });
  },

  trail(g, x, y, size) {
    g.circle(x, y, size * 0.8);
  },
};
